// Copied from platform-app components/pos/receipt/printToBrowser.ts on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
// POS Defaults: no `qrcode` / `jsbarcode` in super-admin-ui — QR via `qrcode.react`
// rendered to static SVG markup, barcode via super-admin-ui's Code 128 encoder.
import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { QRCodeSVG } from 'qrcode.react';
import { code128Geometry } from '../../../../../utils/code128';
import type { PrinterReceiptConfig, ReceiptBlock, BusinessDetailLine } from '@platform/shared-types';
import { DEFAULT_BUSINESS_DETAIL_STYLES } from './blockDefaults';
import type { ReceiptOrder } from './ReceiptPreview';

// Generate a Code 128 barcode as an SVG data URL (browser-side)
function generateBarcodeSvgDataUrl(value: string): string {
  try {
    const { bars, modules } = code128Geometry(value);
    if (!modules) return '';
    const h = 50, text = 14, mw = 2;
    const rects = bars.map((bar) => `<rect x="${bar.x * mw}" y="0" width="${bar.width * mw}" height="${h}" fill="#000"/>`).join('');
    const w = modules * mw;
    const xml = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h + text + 4}" viewBox="0 0 ${w} ${h + text + 4}" shape-rendering="crispEdges"><rect width="100%" height="100%" fill="#fff"/>${rects}<text x="${w / 2}" y="${h + text}" font-family="monospace" font-size="${text}" text-anchor="middle">${escapeHtml(value)}</text></svg>`;
    return 'data:image/svg+xml;utf8,' + encodeURIComponent(xml);
  } catch (err) {
    console.error('[printToBrowser] barcode generation failed', err);
    return '';
  }
}

function qrSvgDataUrl(data: string): string {
  const markup = renderToStaticMarkup(createElement(QRCodeSVG, { value: data, size: 256, level: 'M', marginSize: 1, fgColor: '#000000', bgColor: '#ffffff' }));
  const xml = markup.includes('xmlns=') ? markup : markup.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
  return 'data:image/svg+xml;utf8,' + encodeURIComponent(xml);
}

function businessLineHtml(
  line: BusinessDetailLine | null | undefined,
  defaultContent: string | undefined,
  styleKey: keyof typeof DEFAULT_BUSINESS_DETAIL_STYLES,
): string {
  const def = DEFAULT_BUSINESS_DETAIL_STYLES[styleKey];
  const o = line || {};
  const content = (o.content && o.content.trim()) || defaultContent;
  if (!content) return '';
  const size = o.size ?? def.size;
  const align = o.align ?? def.align;
  const bold = o.bold ?? def.bold;
  const italic = o.italic ?? def.italic;
  const underline = o.underline ?? def.underline;
  const sizeCls = ({ S: 'sm', N: 'n', L: 'lg', XL: 'xl' }[size]);
  const alignCls = ({ L: 'left', C: 'center', R: 'right' }[align]);
  const cls = [sizeCls, alignCls, bold && 'b', italic && 'i', underline && 'u'].filter(Boolean).join(' ');
  return `<div class="${cls}">${escapeHtml(content)}</div>`;
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const sizeClass = (s: 'S' | 'N' | 'L' | 'XL') => ({ S: 'sm', N: 'n', L: 'lg', XL: 'xl' }[s]);
const alignClass = (a: 'L' | 'C' | 'R') => ({ L: 'left', C: 'center', R: 'right' }[a]);
const spacingMm = (s: 'none' | 'S' | 'L') => ({ none: 0, S: 1, L: 3 }[s]);
const dividerCls = (s: 'dashed' | 'solid' | 'double') => `hr-${s}`;

function blockToHtml(b: ReceiptBlock, logoUrl: string | null, qrCache: Map<string, string>): string {
  const top = 'spacingAbove' in b && b.spacingAbove !== 'none'
    ? `<div style="height:${spacingMm(b.spacingAbove)}mm"></div>`
    : '';
  switch (b.type) {
    case 'text': {
      const cls = [sizeClass(b.size), alignClass(b.align), b.bold && 'b', b.italic && 'i', b.underline && 'u', b.inverse && 'inv'].filter(Boolean).join(' ');
      return `${top}<div class="${cls}">${escapeHtml(b.content)}</div>`;
    }
    case 'boxed': {
      const cls = [sizeClass(b.size), alignClass(b.align), b.bold && 'b', `box-${b.borderStyle}`].filter(Boolean).join(' ');
      return `${top}<div class="boxed ${cls}">${escapeHtml(b.content)}</div>`;
    }
    case 'logo': {
      if (!logoUrl) return '';
      const w = ({ S: '20mm', M: '35mm', L: '50mm' }[b.maxWidth]);
      return `${top}<div class="${alignClass(b.align)}"><img src="${escapeHtml(logoUrl)}" alt="" style="max-width:${w};max-height:18mm;display:inline-block" /></div>`;
    }
    case 'image': {
      const w = ({ S: '20mm', M: '35mm', L: '50mm' }[b.maxWidth]);
      return `${top}<div class="${alignClass(b.align)}"><img src="${escapeHtml(b.imageUrl)}" alt="" style="max-width:${w};max-height:25mm;display:inline-block" /></div>`;
    }
    case 'qr': {
      const dim = ({ S: '15mm', M: '22mm', L: '30mm' }[b.size]);
      const cap = b.caption ? `<div class="sm center" style="margin-top:1mm">${escapeHtml(b.caption)}</div>` : '';
      const dataUrl = qrCache.get(b.id);
      const inner = dataUrl
        ? `<img class="qr-img" src="${dataUrl}" alt="QR" style="width:${dim};height:${dim};display:inline-block" />`
        : `<div class="qr" style="width:${dim};height:${dim}"></div>`;
      return `${top}<div class="${alignClass(b.align)}">${inner}${cap}</div>`;
    }
    case 'divider':
      return `${top}<hr class="${dividerCls(b.style)}" />`;
    case 'spacer': {
      const h = ({ S: 1, M: 3, L: 6 }[b.height]);
      return `<div style="height:${h}mm"></div>`;
    }
  }
}

const fmt = (n: number) => n.toFixed(2);

export async function buildReceiptHtml(config: PrinterReceiptConfig, order: ReceiptOrder, width: '80mm' | '58mm'): Promise<string> {
  // Per-line styled business details
  const ov = config.businessDetailsOverride || {};
  const businessDetailsHtml = [
    businessLineHtml(ov.legalName,    order.legalName, 'legalName'),
    businessLineHtml(ov.addressLine1, order.address,   'addressLine1'),
    businessLineHtml(ov.addressLine2, undefined,       'addressLine2'),
    businessLineHtml(ov.addressLine3, undefined,       'addressLine3'),
    businessLineHtml(ov.phone,        order.phone,     'phone'),
  ].join('');

  // Order barcode
  const barcodeUrl = generateBarcodeSvgDataUrl(order.orderNumber);
  const barcodeHtml = barcodeUrl
    ? `<div class="center" style="margin: 2mm 0;"><img src="${barcodeUrl}" alt="Barcode ${escapeHtml(order.orderNumber)}" style="max-width: 100%; height: 50px;" /></div>`
    : '';

  // Pre-generate every QR block as a data: URL so the iframe renders synchronously.
  const allBlocks = [...config.slots.header, ...config.slots.belowItems, ...config.slots.footer];
  const qrCache = new Map<string, string>();
  await Promise.all(
    allBlocks
      .filter((b): b is Extract<ReceiptBlock, { type: 'qr' }> => b.type === 'qr' && !!b.data)
      .map(async (b) => {
        try {
          const dataUrl = qrSvgDataUrl(b.data);
          qrCache.set(b.id, dataUrl);
        } catch (err) {
          console.error('[printToBrowser] QR generation failed for block', b.id, err);
        }
      })
  );

  const headerSlot = config.slots.header.map((b) => blockToHtml(b, config.logoUrl, qrCache)).join('');
  const belowSlot = config.slots.belowItems.map((b) => blockToHtml(b, config.logoUrl, qrCache)).join('');
  const footerSlot = config.slots.footer.map((b) => blockToHtml(b, config.logoUrl, qrCache)).join('');

  const itemsHtml = order.items.map((it) => {
    const left = `${it.qty} × ${escapeHtml(it.name)}`;
    const price = fmt(it.qty * it.price);
    return `<div class="row"><span>${left}</span><span class="dots"></span><span>${price}</span></div>`;
  }).join('');

  const feesHtml = (order.fees || []).map((f) => `<div class="right">${escapeHtml(f.label)}: ${fmt(f.amount)}</div>`).join('');
  const surchHtml = (order.surcharges || []).map((s) => `<div class="right">${escapeHtml(s.label)}: ${fmt(s.amount)}</div>`).join('');

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><title>Receipt</title>
<style>
  @page { size: ${width} auto; margin: 0; }
  @media print {
    html, body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
  html, body { background: #fff; color: #000; }
  body {
    width: ${width};
    margin: 0 auto;
    font-family: 'Courier New', ui-monospace, monospace;
    font-size: 12px;
    line-height: 1.35;
    padding: 3mm 3mm 6mm;
  }
  .center { text-align: center; }
  .right  { text-align: right; }
  .left   { text-align: left; }
  .b      { font-weight: 700; }
  .i      { font-style: italic; }
  .u      { text-decoration: underline; }
  .inv    { background: #000; color: #fff; padding: 0 2px; }
  .sm     { font-size: 10px; }
  .n      { font-size: 12px; }
  .lg     { font-size: 14px; }
  .xl     { font-size: 16px; }
  hr.hr-dashed { border: 0; border-top: 1px dashed #000; margin: 1mm 0; }
  hr.hr-solid  { border: 0; border-top: 1px solid #000; margin: 1mm 0; }
  hr.hr-double { border: 0; border-top: 3px double #000; margin: 1mm 0; }
  .boxed { border: 1px solid #000; padding: 1mm 2mm; margin: 1mm 0; }
  .boxed.box-dashed { border-style: dashed; }
  .boxed.box-double { border-style: double; border-width: 3px; }
  .row { display: flex; align-items: baseline; gap: 1mm; }
  .row .dots { flex: 1; border-bottom: 1px dotted #000; transform: translateY(-2px); height: 1px; }
  .qr { background: repeating-linear-gradient(45deg, #000 0 2px, transparent 2px 4px); border: 4px solid #000; margin: 0 auto; }
  /* Keep logos / QR / images crisp on thermal drivers — disable browser/driver smoothing */
  img, .qr-img {
    image-rendering: pixelated;
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
    -ms-interpolation-mode: nearest-neighbor;
    filter: contrast(1.1);
  }
  .totals .right { font-variant-numeric: tabular-nums; }
</style>
</head><body>
${headerSlot}
${businessDetailsHtml}
<hr class="hr-solid" />

<div>Order #${escapeHtml(order.orderNumber)}</div>
<div class="sm">${new Date(order.timestamp).toLocaleString()}</div>
${order.cashier ? `<div class="sm">Cashier: ${escapeHtml(order.cashier)}</div>` : ''}
<hr class="hr-dashed" />

<div class="items">${itemsHtml}</div>

${belowSlot}

<hr class="hr-dashed" />
<div class="totals">
  <div class="right">Subtotal: ${fmt(order.subtotal)}</div>
  <div class="right">Tax: ${fmt(order.tax)}</div>
  ${feesHtml}
  ${surchHtml}
  ${order.tip != null ? `<div class="right">Tip: ${fmt(order.tip)}</div>` : ''}
  <div class="right b lg">TOTAL: ${fmt(order.total)}</div>
  <div class="right sm">${escapeHtml(order.payment)}${order.change != null ? ` · Change ${fmt(order.change)}` : ''}</div>
</div>

${barcodeHtml}
${footerSlot}
</body></html>`;
}

/**
 * Send a fully-rendered receipt HTML doc to the browser's print dialog
 * via a hidden iframe. Works with any installed Windows printer driver
 * (RONGTA, Star, Epson, etc.). When Chrome is launched with the
 * --kiosk-printing flag, the dialog is skipped and the doc goes silent
 * to the default printer.
 *
 * Returns a Promise that resolves once the print dialog has been triggered
 * (NOT once paper has come out — there is no browser API for that).
 */
export function printToBrowser(html: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.opacity = '0';
    document.body.appendChild(iframe);

    const cleanup = () => {
      try { document.body.removeChild(iframe); } catch { /* already removed */ }
    };

    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (!win) throw new Error('iframe.contentWindow unavailable');
        win.focus();
        // afterprint fires after Chrome dismisses the dialog (Print or Cancel).
        // Some browsers don't fire it reliably, so we also fall back to a timeout.
        const onAfter = () => { win.removeEventListener('afterprint', onAfter); cleanup(); resolve(); };
        win.addEventListener('afterprint', onAfter);
        win.print();
        // Safety net for browsers that don't fire afterprint (Safari)
        setTimeout(() => { cleanup(); resolve(); }, 30_000);
      } catch (err) {
        cleanup();
        reject(err);
      }
    };

    // srcdoc fires onload reliably in modern browsers
    iframe.srcdoc = html;
  });
}
